import { Button, Input } from '@bs/ui-components'
import { LoaderCircle } from 'lucide-react'
import { FormEvent, useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { AuthPageShell } from './auth-page-shell'
import { useAuth } from '@/app/use-auth'
import { consumeAuthRedirect } from '@/services/redirect-store'

export function LoginPage() {
  const { error, isAuthenticated, signIn, clearError } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const passwordChanged = searchParams.get('passwordChanged') === '1'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (isAuthenticated) navigate(consumeAuthRedirect(), { replace: true })
  }, [isAuthenticated, navigate])

  async function login(event: FormEvent) {
    event.preventDefault()
    setIsSubmitting(true)
    clearError()
    try {
      await signIn(email, password)
      navigate(consumeAuthRedirect(), { replace: true })
    } catch {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthPageShell>
      <form onSubmit={login} className="w-full max-w-[500px]">
            <h1 className="mb-10 text-center text-[30px] font-medium leading-[1.3] text-[#404040]">
              Sign In
            </h1>

            {passwordChanged && !error ? (
              <div
                role="status"
                className="mb-6 rounded-xl border border-[#00895b]/25 bg-[#00895b]/5 px-4 py-3 text-sm text-[#00895b]"
              >
                Your password was changed. Log in with your new password.
              </div>
            ) : null}

            {error ? (
              <div
                role="alert"
                className="mb-6 rounded-xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive"
              >
                {error}
              </div>
            ) : null}

            <div className="mb-6">
              <label htmlFor="login-email" className="mb-4 block text-base text-[#404040]">
                Email
              </label>
              <Input
                id="login-email"
                type="email"
                placeholder="Enter email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="h-11 rounded-xl px-4"
                required
              />
            </div>

            <div className="mb-10">
              <label htmlFor="login-password" className="mb-4 block text-base text-[#404040]">
                Password
              </label>
              <Input
                id="login-password"
                type="password"
                placeholder="Enter password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="h-11 rounded-xl px-4"
                required
              />
            </div>

            <Button
              type="submit"
              variant="accent"
              size="lg"
              className="h-[50px] w-full text-lg font-medium"
              disabled={isSubmitting}
            >
              {isSubmitting ? <LoaderCircle className="size-5 animate-spin" /> : null}
              {isSubmitting ? 'Signing in…' : 'Log in'}
            </Button>

            <p className="mt-10 text-center text-base text-[#8a8a8a]">
              Forgot password?{' '}
              <Link to="/forgot-password" className="text-primary hover:underline">
                Click here
              </Link>
            </p>
      </form>
    </AuthPageShell>
  )
}
